import {
  ArrowLeftIcon,
  HeartIcon,
  UserIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { BotonAuth, CampoAuth } from '@/components/CampoAuth'
import { Logo } from '@/components/Logo'
import { ApiError, type FieldErrors, type HouseholdKind, auth } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Acceso y alta.
 *
 * Tres pantallas y no una: bienvenida, entrar y crear cuenta. Antes eran un
 * solo formulario con un conmutador, y eso obliga a decidir «entro o me
 * registro» leyendo campos en vez de leyendo una pregunta. Con la bienvenida
 * delante, la decisión se toma en dos botones y cada formulario puede abrir con
 * su propio titular.
 *
 * El tipo de hogar se elige al crear la cuenta, pero no es definitivo: un hogar
 * personal sube solo a pareja al invitar a alguien, y de pareja a familia con
 * el tercer integrante. Se dice en la propia pantalla porque la duda en un
 * formulario de registro es lo que hace que se abandone.
 */

type Vista = 'bienvenida' | 'entrar' | 'crear'

export function Auth({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [vista, setVista] = useState<Vista>('bienvenida')

  if (vista === 'bienvenida') {
    return <Bienvenida onEntrar={() => setVista('entrar')} onCrear={() => setVista('crear')} />
  }

  return (
    <Pagina onVolver={() => setVista('bienvenida')}>
      {vista === 'entrar' ? (
        <FormularioAcceso onAuthenticated={onAuthenticated} onCrear={() => setVista('crear')} />
      ) : (
        <FormularioAlta onAuthenticated={onAuthenticated} onEntrar={() => setVista('entrar')} />
      )}
    </Pagina>
  )
}

/**
 * La bienvenida.
 *
 * Una marca grande, una frase que dice para qué sirve la app y dos caminos. No
 * hay «continuar como invitado»: PayBae no tiene modo invitado —el dinero
 * necesita una cuenta a la que pertenecer— y ofrecer un botón que no existe es
 * peor que no ofrecerlo.
 */
function Bienvenida({ onEntrar, onCrear }: { onEntrar: () => void; onCrear: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      {/* El lienzo de arriba: el mismo halo del inicio, para que la primera
          pantalla ya se parezca a la app y no a un trámite previo. */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 80% at 70% 20%, color-mix(in srgb, var(--primary) 26%, transparent), transparent 60%),' +
              'radial-gradient(90% 70% at 20% 70%, color-mix(in srgb, var(--income) 22%, transparent), transparent 62%)',
          }}
        />
        {/* `Wordmark` ya trae el logotipo dentro; poner además `Logo` dibujaba
            la marca dos veces. */}
        <div className="relative flex flex-col items-center gap-6 px-8 text-center">
          <Logo className="size-20" />
          <h1 className="font-display text-[34px] font-extrabold tracking-[-0.04em] text-ink">
            PayBae
          </h1>
          <p className="max-w-[18rem] text-[15px] leading-snug text-ink-soft">
            Las finanzas de los dos, sin perder de vista lo tuyo.
          </p>
        </div>
      </div>

      <div className="px-safe pb-safe">
        <div className="mx-auto flex w-full max-w-sm flex-col gap-3 px-6 pb-8">
          <BotonAuth type="button" onClick={onEntrar}>
            Entrar
          </BotonAuth>
          <BotonAuth type="button" variante="contorno" onClick={onCrear}>
            Crear cuenta
          </BotonAuth>
        </div>
      </div>
    </div>
  )
}

/** El marco de las dos pantallas de formulario: flecha atrás y contenido. */
function Pagina({ onVolver, children }: { onVolver: () => void; children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <div className="px-safe mx-auto w-full max-w-sm px-6 pt-safe">
        <header className="py-4">
          <button
            type="button"
            onClick={() => {
              haptic('light')
              onVolver()
            }}
            aria-label="Volver"
            className="flex size-11 items-center justify-center rounded-full border border-line bg-surface text-ink active:scale-95"
          >
            <ArrowLeftIcon size={18} />
          </button>
        </header>
        {children}
      </div>
    </div>
  )
}

function Titular({ children }: { children: string }) {
  return (
    <h1 className="mb-7 mt-3 font-display text-[30px] font-extrabold leading-[1.08] tracking-[-0.04em] text-ink">
      {children}
    </h1>
  )
}

function PieDeCambio({
  pregunta,
  accion,
  onClick,
}: {
  pregunta: string
  accion: string
  onClick: () => void
}) {
  return (
    <p className="py-8 text-center text-[14px] text-ink-soft">
      {pregunta}{' '}
      <button
        type="button"
        onClick={onClick}
        className="min-h-[44px] font-semibold text-primary-text active:opacity-70"
      >
        {accion}
      </button>
    </p>
  )
}

function AvisoError({ mensaje }: { mensaje: string | null }) {
  if (!mensaje) return null
  return (
    <p role="alert" className="rounded-control bg-expense-soft px-4 py-3 text-[13px] text-expense-text">
      {mensaje}
    </p>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Entrar
// ═══════════════════════════════════════════════════════════════════════════

function FormularioAcceso({
  onAuthenticated,
  onCrear,
}: {
  onAuthenticated: () => void
  onCrear: () => void
}) {
  const navigate = useNavigate()
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [campos, setCampos] = useState<FieldErrors>({})

  const enviar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setEnviando(true)
    setError(null)
    setCampos({})

    const datos = new FormData(event.currentTarget)
    try {
      await auth.login(String(datos.get('email')), String(datos.get('password')))
      haptic('success')
      onAuthenticated()
    } catch (cause) {
      haptic('error')
      if (cause instanceof ApiError) {
        setError(cause.message)
        setCampos(cause.fieldErrors ?? {})
      } else {
        setError('Algo salió mal. Inténtalo de nuevo.')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <Titular>Qué gusto verte de nuevo</Titular>

      <form onSubmit={enviar} className="flex flex-col gap-3" noValidate>
        <CampoAuth
          label="Tu correo"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          errores={campos.email}
        />
        <CampoAuth
          label="Tu contraseña"
          name="password"
          type="password"
          autoComplete="current-password"
          errores={campos.password}
        />

        {/* Debajo de la contraseña y alineado a la derecha: es donde se busca
            justo después de fallar el intento, que es cuando hace falta. */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              haptic('light')
              navigate('/recuperar')
            }}
            className="-mr-1 min-h-[44px] px-1 text-[13px] font-semibold text-primary-text active:opacity-70"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        <AvisoError mensaje={error} />
        <div className="mt-1">
          <BotonAuth enviando={enviando}>Entrar</BotonAuth>
        </div>
      </form>

      <PieDeCambio pregunta="¿Todavía no tienes cuenta?" accion="Crea una" onClick={onCrear} />
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Crear cuenta
// ═══════════════════════════════════════════════════════════════════════════

const TIPOS: ReadonlyArray<{
  value: HouseholdKind
  label: string
  detalle: string
  Icon: typeof UserIcon
}> = [
  { value: 'PERSONAL', label: 'Personal', detalle: 'Solo tú', Icon: UserIcon },
  { value: 'COUPLE', label: 'Pareja', detalle: 'Los dos', Icon: HeartIcon },
  { value: 'GROUP', label: 'Familia', detalle: 'Tres o más', Icon: UsersThreeIcon },
]

function FormularioAlta({
  onAuthenticated,
  onEntrar,
}: {
  onAuthenticated: () => void
  onEntrar: () => void
}) {
  const [kind, setKind] = useState<HouseholdKind>('COUPLE')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [campos, setCampos] = useState<FieldErrors>({})

  const enviar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setEnviando(true)
    setError(null)
    setCampos({})

    const datos = new FormData(event.currentTarget)
    const clave = String(datos.get('password'))

    // La confirmación se compara aquí y no en el servidor: el servidor no la
    // recibe —no tiene por qué—, y el error tiene que salir junto al campo que
    // lo causó, no después de un viaje de ida y vuelta.
    if (clave !== String(datos.get('passwordConfirm'))) {
      setEnviando(false)
      setCampos({ passwordConfirm: ['Las dos contraseñas no coinciden.'] })
      haptic('error')
      return
    }

    try {
      await auth.register({
        email: String(datos.get('email')),
        password: clave,
        displayName: String(datos.get('displayName')),
        householdName: String(datos.get('householdName')),
        householdKind: kind,
      })
      haptic('success')
      onAuthenticated()
    } catch (cause) {
      haptic('error')
      if (cause instanceof ApiError) {
        setError(cause.message)
        setCampos(cause.fieldErrors ?? {})
      } else {
        setError('Algo salió mal. Inténtalo de nuevo.')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <>
      <Titular>Crea tu cuenta y empecemos</Titular>

      <form onSubmit={enviar} className="flex flex-col gap-3" noValidate>
        <CampoAuth
          label="Tu nombre"
          name="displayName"
          autoComplete="name"
          errores={campos.displayName}
        />
        <CampoAuth
          label="Tu correo"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          errores={campos.email}
        />
        <CampoAuth
          label="Contraseña"
          name="password"
          type="password"
          autoComplete="new-password"
          ayuda="Mínimo 10 caracteres."
          errores={campos.password}
        />
        <CampoAuth
          label="Repite la contraseña"
          name="passwordConfirm"
          type="password"
          autoComplete="new-password"
          errores={campos.passwordConfirm}
        />

        {/* El hogar, separado de la cuenta por un rótulo: son dos cosas
            distintas —quién eres y qué van a administrar— y mezclarlas en una
            lista corrida de seis campos hace que parezca el doble de larga. */}
        <p className="mt-4 px-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-faint">
          Tu hogar
        </p>

        <CampoAuth
          label="Cómo se llama"
          name="householdName"
          ayuda="Como lo verán todos los integrantes."
          errores={campos.householdName}
        />

        <fieldset className="mt-1">
          <legend className="sr-only">¿Cómo lo vas a usar?</legend>
          <div className="grid grid-cols-3 gap-2">
            {TIPOS.map((tipo) => {
              const activo = kind === tipo.value
              return (
                <button
                  key={tipo.value}
                  type="button"
                  onClick={() => {
                    haptic('selection')
                    setKind(tipo.value)
                  }}
                  aria-pressed={activo}
                  className={cn(
                    'flex min-h-[80px] flex-col items-center justify-center gap-1 rounded-control border px-2 py-3',
                    'transition-colors duration-150 active:scale-95',
                    activo ? 'border-primary bg-primary-soft' : 'border-transparent bg-surface-sunken',
                  )}
                >
                  <tipo.Icon
                    size={20}
                    weight={activo ? 'fill' : 'regular'}
                    className={activo ? 'text-primary-text' : 'text-ink-faint'}
                  />
                  <span
                    className={cn(
                      'text-[13px] font-semibold',
                      activo ? 'text-ink' : 'text-ink-soft',
                    )}
                  >
                    {tipo.label}
                  </span>
                  <span className="text-center text-[10px] leading-tight text-ink-faint">
                    {tipo.detalle}
                  </span>
                </button>
              )
            })}
          </div>
          <p className="mt-2 px-1 text-[12px] leading-snug text-ink-faint">
            Se ajusta solo: al invitar a alguien, un hogar personal pasa a pareja, y de pareja
            a familia con el tercer integrante.
          </p>
        </fieldset>

        <AvisoError mensaje={error} />
        <div className="mt-3">
          <BotonAuth enviando={enviando}>Crear mi hogar</BotonAuth>
        </div>
      </form>

      <PieDeCambio pregunta="¿Ya tienes cuenta?" accion="Entra" onClick={onEntrar} />
    </>
  )
}
